import { Phone, MapPin, ShieldCheck, HardHat, Hammer } from "lucide-react";
import { BRAND, IMAGES } from "../data/site";

const About = () => {
  return (
    <div data-testid="about-page" className="pt-32">
      {/* Header */}
      <section className="bg-stone-50 pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="uppercase text-xs tracking-[0.25em] text-stone-500 font-semibold mb-6">
            About
          </p>
          <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight text-stone-900 leading-[1.02] max-w-5xl">
            A Pacific Northwest builder
            <span className="block italic text-[#9B4819]">
              with {BRAND.yearsInBusiness} years on the tools.
            </span>
          </h1>
        </div>
      </section>

      {/* Story image + text */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-6">
            <div className="relative overflow-hidden">
              <img
                src={IMAGES.kitchen}
                alt="Morgan Construction Group remodel"
                className="w-full h-[520px] object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-6 lg:pl-6">
            <p className="uppercase text-xs tracking-[0.25em] text-stone-500 font-semibold mb-5">
              Our story
            </p>
            <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-stone-900 leading-[1.1]">
              Built one kitchen, one bathroom, one living room at a time.
            </h2>
            <div className="mt-8 space-y-6 text-stone-700 text-base sm:text-lg leading-relaxed max-w-xl">
              <p>
                Morgan Construction Group was founded on a simple idea: homeowners
                deserve a builder who treats their project like it's their own.
                For {BRAND.yearsInBusiness} years we've been remodeling homes across
                Oregon and Washington — from modest updates to full-home renovations.
              </p>
              <p>
                We specialize in tile-forward design: big-format kitchen tile, stone
                bathroom showers, and seamless living-room floors. Every project is
                led by a hands-on project manager, with a dedicated crew from
                demolition through final walk-through.
              </p>
              <p>
                If it's worth doing, it's worth doing right the first time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-stone-50 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="uppercase text-xs tracking-[0.25em] text-stone-500 font-semibold mb-6">
            What we stand for
          </p>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-stone-900 leading-[1.05] max-w-3xl">
            Craft. Trust. Timelines.
          </h2>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                icon: HardHat,
                title: "Craftsmanship",
                desc:
                  "We care about the millimeter. Tile lines, cabinet reveals, grout joints — the details are the work.",
              },
              {
                icon: ShieldCheck,
                title: "Trust",
                desc:
                  "Licensed, bonded, insured. Fixed quotes, clear scopes, honest answers when something changes.",
              },
              {
                icon: Hammer,
                title: "Timelines",
                desc:
                  "We show up when we say we'll show up. One lead, one crew, one clear completion date.",
              },
            ].map((v) => (
              <div
                key={v.title}
                data-testid={`value-${v.title.toLowerCase()}`}
                className="bg-white border border-stone-200 p-10 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(28,25,23,0.15)]"
              >
                <v.icon className="w-7 h-7 text-[#9B4819]" strokeWidth={1.75} />
                <h3 className="font-display text-2xl text-stone-900 mt-6 mb-3">
                  {v.title}
                </h3>
                <p className="text-stone-600 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service area + stats */}
      <section className="bg-stone-900 text-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="uppercase text-xs tracking-[0.3em] text-[#E8A976] mb-6 font-semibold">
              Service area
            </p>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Serving Oregon
              <span className="block italic text-[#E8A976]">& Washington.</span>
            </h2>
            <div className="mt-10 flex items-center gap-3 text-stone-300">
              <MapPin className="w-5 h-5 text-[#E8A976]" />
              <span>Portland · Beaverton · Hillsboro · Lake Oswego · Vancouver · Seattle · Bellevue</span>
            </div>
            <a
              href={BRAND.phoneLink}
              data-testid="about-phone-cta"
              className="mt-10 inline-flex items-center gap-3 bg-[#9B4819] hover:bg-[#7A3813] text-white px-7 py-4 text-sm font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5"
            >
              <Phone className="w-4 h-4" />
              {BRAND.phoneDisplay}
            </a>
          </div>

          <div className="lg:col-span-6 grid grid-cols-2 gap-px bg-stone-800">
            {[
              { n: `${BRAND.yearsInBusiness}+`, l: "Years in business", size: "text-5xl" },
              { n: "2", l: "States served", size: "text-5xl" },
              { n: `#${BRAND.ccbLicense}`, l: "Oregon CCB license", size: "text-3xl sm:text-4xl" },
              { n: "1:1", l: "Project-lead ratio", size: "text-5xl" },
            ].map((s) => (
              <div key={s.l} className="bg-stone-900 p-8">
                <div className={`font-display ${s.size} font-light text-[#E8A976] leading-tight break-all`}>
                  {s.n}
                </div>
                <div className="uppercase text-[11px] tracking-[0.2em] text-stone-400 mt-3">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
